import {
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalTableWithControls,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell, TableRow } from "@mui/material";
import React from "react";
import { useTranslation } from "../../../localization/useTranslation";
import { useMasterDataBreadCrumbProvider } from "../../useMasterDataBreadCrumbProvider";
import HelpTextDeleteDialog from "../helpTextDeleteDialog/HelpTextDeleteDialog";
import HelpTextDialog from "../helpTextDialog/HelpTextDialog";
import { useHelpTextDataMapping } from "./HelpTexts.hooks";
import { HelpTextViewModel } from "./HelpTexts.types";
import EmptyStateSvg from "../../../system/systems/noSystems.svg?react";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function HelpTexts() {
    const { getBreadCrumbs } = useMasterDataBreadCrumbProvider();
    const { translateString, translateHelpTextTypes } = useTranslation();
    const {
        data,
        isLoading,
        refetch,
        setShowDialog,
        showDialog,
        selectedHelpText,
        setSelectedHelpText,
        getEmptyHelpText,
        showDeleteDialog,
        setShowDeleteDialog,
        hasHelpTexts,
    } = useHelpTextDataMapping();

    const getMenuItems = (row: HelpTextViewModel) => {
        let menuItems = [];

        menuItems.push({
            menuItem: translateString("edit"),
            onClick: () => {
                setSelectedHelpText({ content: row.content, header: row.header, id: row.id, type: row.type });
                setShowDialog(true);
            },
        });

        menuItems.push({
            menuItem: translateString("delete"),
            onClick: () => {
                setSelectedHelpText({ content: "", header: row.header, id: row.id, type: undefined });
                setShowDeleteDialog(true);
            },
        });

        return menuItems;
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs(translateString("helpTexts"))}
                rightComponent={
                    <DotLegalButton
                        buttonType="primary"
                        size="medium"
                        onClick={() => {
                            setSelectedHelpText(getEmptyHelpText());
                            setShowDialog(true);
                        }}
                    >
                        {translateString("createHelpText")}
                    </DotLegalButton>
                }
            />

            <TablePagePaper>
                {hasHelpTexts ? (
                    <DotLegalTableWithControls
                        internalScroll
                        headers={getHeaders(translateString)}
                        getUserSpecificPageLength={() => 30}
                        hideRowsPerPage
                        defaultOrderBy={"header"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        hasOverflowMenu
                        emptyText={translateString("noData")}
                        labelRowsPerPage={translateString("showEntites")}
                        labelSearch={translateString("search")}
                        noOptionsLabel={translateString("noOptions")}
                        paginationLabelOf={translateString("labelOf")}
                        data={data}
                        renderRow={(row) => (
                            <TableRow key={row.id}>
                                <TableCell>{row.header}</TableCell>
                                <TableCell>{row.content}</TableCell>
                                <TableCell>{translateHelpTextTypes(row.type.toString())}</TableCell>
                                <TableCell align={"right"}>
                                    <DotLegalOverflowMenu items={getMenuItems(row)} />
                                </TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<EmptyStateSvg />} text={translateString("noHelpTexts")} />
                )}
            </TablePagePaper>

            {showDialog && (
                <HelpTextDialog
                    onDialogClose={() => setShowDialog(false)}
                    onSave={() => refetch()}
                    selectedHelpText={selectedHelpText}
                    usedHelpTextTypes={data?.map((x) => x.type) ?? []}
                ></HelpTextDialog>
            )}

            {showDeleteDialog && (
                <HelpTextDeleteDialog closeDialog={() => setShowDeleteDialog(false)} onDeleted={() => refetch()} selectedHelpText={selectedHelpText} />
            )}
        </Box>
    );
}

export default HelpTexts;

function getHeaders(translateString: any) {
    let headers = Array<ITableHeader<HelpTextViewModel>>();
    headers.push({ property: "header", text: translateString("header"), align: "left", showOnMobile: true });
    headers.push({ property: "content", text: translateString("content"), align: "left", showOnMobile: true });
    headers.push({ property: "type", text: translateString("type"), align: "left", showOnMobile: true });

    return headers;
}
